import { z } from 'zod';

/**
 * Per-agency delivery destinations (migration 0052) — the recipient directory a
 * forward (SITREP §4.19, assistance request §4.20, dispatch recommendation §4.18)
 * is routed to. Closes the PRD §6 "one webhook, no routing" boundary.
 */

/** Recipient category. */
export const DELIVERY_DESTINATION_KINDS = [
  'peoc',
  'ministry',
  'municipality',
  'agency',
  'federal',
] as const;
export type DeliveryDestinationKind = (typeof DELIVERY_DESTINATION_KINDS)[number];

/** Delivery channel. */
export const DELIVERY_DESTINATION_CHANNELS = ['webhook', 'email'] as const;
export type DeliveryDestinationChannel = (typeof DELIVERY_DESTINATION_CHANNELS)[number];

/** A registered destination, as returned by the API. */
export const DeliveryDestinationSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.enum(DELIVERY_DESTINATION_KINDS),
  channel: z.enum(DELIVERY_DESTINATION_CHANNELS),
  /** Webhook URL or email address; null on the default PEOC row (env-resolved). */
  address: z.string().nullable(),
  /** The "who to call" number the Notify-PEOC surface shows (migration 0124; nullable). */
  contact_phone: z.string().nullable().optional(),
  active: z.boolean(),
  is_default: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type DeliveryDestination = z.infer<typeof DeliveryDestinationSchema>;

/** Create body. address is required for an email destination; optional for a
 *  webhook (a NULL-address webhook resolves to DELIVERY_WEBHOOK_URL). */
export const DeliveryDestinationCreateSchema = z
  .object({
    name: z.string().min(1).max(200),
    kind: z.enum(DELIVERY_DESTINATION_KINDS),
    channel: z.enum(DELIVERY_DESTINATION_CHANNELS),
    address: z.string().max(2000).nullable().optional(),
    /** The "who to call" number the out-of-app contact surfaces (migration 0124;
     *  nullable). #328 — writable so an operator can register an official's phone. */
    contact_phone: z.string().max(64).nullable().optional(),
    active: z.boolean().optional(),
    is_default: z.boolean().optional(),
  })
  .refine((b) => b.channel !== 'email' || (b.address && b.address.length > 0), {
    message: 'email destinations require an address',
    path: ['address'],
  });
export type DeliveryDestinationCreate = z.infer<typeof DeliveryDestinationCreateSchema>;

/** Partial update — any field; at least one must be present. */
export const DeliveryDestinationUpdateSchema = z
  .object({
    name: z.string().min(1).max(200).optional(),
    kind: z.enum(DELIVERY_DESTINATION_KINDS).optional(),
    channel: z.enum(DELIVERY_DESTINATION_CHANNELS).optional(),
    address: z.string().max(2000).nullable().optional(),
    contact_phone: z.string().max(64).nullable().optional(),
    active: z.boolean().optional(),
    is_default: z.boolean().optional(),
  })
  .refine((b) => Object.keys(b).length > 0, { message: 'no fields to update' });
export type DeliveryDestinationUpdate = z.infer<typeof DeliveryDestinationUpdateSchema>;

export const DeliveryDestinationListSchema = z.object({
  destinations: z.array(DeliveryDestinationSchema),
});
export type DeliveryDestinationList = z.infer<typeof DeliveryDestinationListSchema>;
